/**
 * Resumen de cabecera del pedido de cliente (petición del CEO, 26/10/2026):
 * referencias, unidades, importe sin y con IVA y fecha estimada de entrega,
 * para ver el total del pedido de un vistazo.
 *
 * Las líneas canceladas no cuentan. Unidades e importe usan
 * max(pedidas, entregadas), igual que el subtotal de cada línea (una
 * sobre-entrega se factura).
 */

export type LineaResumen = {
  id: string;
  producto_id: string | null;
  sku_cliente: string | null;
  unidades: number;
  unidades_entregadas: number | null;
  precio_unitario: number | string | null;
  cancelada: boolean;
  eta: string | null;
  pedido_proveedor_eta: string | null;
  fecha_entrega_solicitada: string | null;
  /** Fecha estimada de entrega de la línea (por defecto, la de la cabecera). Manda sobre la ETA. */
  fecha_entrega_estimada?: string | null;
  ultima_entrega_fecha: string | null;
};

export type FuenteEntrega = 'estimada' | 'eta' | 'solicitada';

export type EntregaEstimada =
  /** Todas las líneas entregadas: fecha de la última entrega (si se conoce). */
  | { tipo: 'entregado'; fecha: string | null }
  /** Fecha en que llega la última línea pendiente; `primera` si hay varias fechas distintas. */
  | { tipo: 'estimada'; fecha: string; primera: string | null; fuente: FuenteEntrega | 'mixta'; sinFecha: number }
  /** Hay líneas pendientes y ninguna tiene ETA ni entrega solicitada. */
  | { tipo: 'sin_fecha' };

export type ResumenPedido = {
  referencias: number;
  lineas: number;
  unidades: number;
  unidadesEntregadas: number;
  importeSinIva: number;
  importeConIva: number;
  ivaPct: number;
  entrega: EntregaEstimada;
};

const dia = (s: string | null | undefined): string | null => (s ? String(s).slice(0, 10) : null);

export function resumenPedido(lineas: ReadonlyArray<LineaResumen>, ivaPct: number): ResumenPedido {
  const vivas = lineas.filter((l) => !l.cancelada);
  const refs = new Set(vivas.map((l) => l.producto_id ?? (l.sku_cliente ? `sku:${l.sku_cliente.trim().toUpperCase()}` : `linea:${l.id}`)));
  let unidades = 0;
  let entregadas = 0;
  let importe = 0;
  for (const l of vivas) {
    const ent = Number(l.unidades_entregadas ?? 0);
    const uds = Math.max(Number(l.unidades) || 0, ent);
    unidades += uds;
    entregadas += ent;
    importe += (Number(l.precio_unitario) || 0) * uds;
  }
  const iva = Number.isFinite(ivaPct) ? ivaPct : 21;
  return {
    referencias: refs.size,
    lineas: vivas.length,
    unidades,
    unidadesEntregadas: entregadas,
    importeSinIva: redondear(importe),
    importeConIva: redondear(importe * (1 + iva / 100)),
    ivaPct: iva,
    entrega: entregaEstimada(vivas),
  };
}

function entregaEstimada(vivas: ReadonlyArray<LineaResumen>): EntregaEstimada {
  const pendientes = vivas.filter((l) => Number(l.unidades_entregadas ?? 0) < Number(l.unidades));
  if (vivas.length > 0 && pendientes.length === 0) {
    const fechas = vivas.map((l) => dia(l.ultima_entrega_fecha)).filter((f): f is string => !!f).sort();
    return { tipo: 'entregado', fecha: fechas.at(-1) ?? null };
  }
  // Por línea: su fecha estimada de entrega (cabecera o ajuste a mano); si no
  // hay, la ETA; si tampoco, la entrega solicitada.
  const fuentes = new Set<FuenteEntrega>();
  const fechas: string[] = [];
  let sinFecha = 0;
  for (const l of pendientes) {
    const candidatas: [FuenteEntrega, string | null][] = [
      ['estimada', dia(l.fecha_entrega_estimada)],
      ['eta', dia(l.pedido_proveedor_eta ?? l.eta)],
      ['solicitada', dia(l.fecha_entrega_solicitada)],
    ];
    const hit = candidatas.find(([, f]) => f);
    if (hit) { fechas.push(hit[1]!); fuentes.add(hit[0]); } else sinFecha++;
  }
  if (fechas.length === 0) return { tipo: 'sin_fecha' };
  fechas.sort();
  const primera = fechas[0];
  const ultima = fechas[fechas.length - 1];
  return {
    tipo: 'estimada',
    fecha: ultima,
    primera: primera !== ultima ? primera : null,
    fuente: fuentes.size > 1 ? 'mixta' : [...fuentes][0],
    sinFecha,
  };
}

const redondear = (n: number) => Math.round(n * 100) / 100;
